import { Component, Mixins, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import PageTitle from '@/components/ui/page-title/pageTitle.vue';
import GridTable from '@/components/ui/grid-table/gridTable.vue';
import { GridTableOptions, GridTablePaginationParams } from '@/components/ui/grid-table/gridTable';
import { getShowingDate } from '@/helpers/date.helper';
import { numberFormatter, removeTags } from '@/helpers/string.helper';
import { ReturnOrderListRequest } from '@/types/request/return-order-request.types';
import { ReturnOrderType } from '@/types/api/return-order/return-order.type';
import { getReturnOrderStatus } from '@/constants/return-order-status.constants';
import ActionsCellRenderer from '@/components/ui/grid-table/cell/actions-cell-renderer/actionsCellRenderer.vue';
import { ActionsCellRendererParams } from '@/components/ui/grid-table/cell/actions-cell-renderer/actionsCellRenderer';
import {
  ENTITY_COMMENTS,
  ENTITY_PSP_INVOICE_ORDER,
  ENTITY_RETURN_ORDER,
  ENTITY_TRANSACTION,
  IEntity
} from '@/constants/entity.constants';
import InfoDialogMixin from '@/mixins/info-dialog.mixin';
import CommentDialogMixin from '@/mixins/comment-dialog.mixin';
import CopyCellRenderer from '@/components/ui/grid-table/cell/copy-cell-renderer/copy-cell-renderer.vue';
import { ListResponseType } from '@/types/response/list-response.type';
import XlsExportMixin from '@/mixins/xls-export.mixin';
import { BSpinner, BTooltip } from 'bootstrap-vue';
import ExportXlsxBtn from '@/components/ui/buttons/export-xlsx-btn/export-xlsx-btn.vue';
import { getExternalPaymentStatus } from '@/constants/external-payment-status.constants';
import TransactionsGrid from '@/components/features/grids/transactions-grid/transactions-grid.vue';
import GridMixin from '@/mixins/grid.mixin';
import CopyBtn from '@/components/ui/buttons/copy-btn/copy-btn.vue';
import { IPageTitle } from '@/components/ui/page-title/page-title.interface';

const title = 'returnOrders.title';
const DEFAULT_SORT = {
  sortBy: 'createdAt',
  sortDesc: true
};

@Component({
  name: 'ReturnOrdersGrid',
  components: {
    PageTitle,
    GridTable,
    ActionsCellRenderer,
    CopyCellRenderer,
    ExportXlsxBtn,
    BSpinner,
    TransactionsGrid,
    BTooltip,
    PspInvoiceOrdersGrid: () => import('@/components/features/grids/psp-invoice-orders-grid/psp-invoice-orders-grid.vue'),
    CopyBtn,
  },
})
export default class ReturnOrdersGrid extends Mixins(
  XlsExportMixin,
  InfoDialogMixin,
  CommentDialogMixin,
  GridMixin
) {
  title = title;
  tableName = 'returnOrders';
  tableOptions: GridTableOptions = {
    pagination: {
      callback: this.gridChangeCallback
    },
    sort: DEFAULT_SORT,
    hasDetails: true,
  };
  permissionEntity: IEntity = ENTITY_RETURN_ORDER;
  isShowGrid = true;

  @Prop({
    required: false,
    default: () => ({
      showTitle: false,
      isMainTitle: false,
      class: 'mt-4',
      showDataCount: false,
    })
  })
  titleOptions?: IPageTitle;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  toggled?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  withTransactions?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  withPio?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => true
  })
  withExport?: boolean;

  @Prop({
    required: false,
    default: () => ({})
  })
  gridFilter?: ReturnOrderListRequest;

  @Action('getReturnOrders', { namespace: 'returnOrders' })
  getReturnOrders!: (params?: ReturnOrderListRequest) => Promise<ListResponseType<ReturnOrderType>>;

  @Getter('getReturnOrders', { namespace: 'returnOrders' })
  exportData!: ListResponseType<ReturnOrderType>;

  created() {
    this.filter = this.gridFilter as any;

    if (this.toggled) {
      this.isShowGrid = false;
    }
  }

  gridChangeCallback(params: GridTablePaginationParams) {
    return this.getReturnOrders({
      ...this.paginationFilter(params),
      ...this.prepareFilter(),
    });
  }

  get tableFields() {
    const fields = this.withTransactions || this.withPio
      ? [{
        label: '',
        key: 'details',
        thStyle: { width: '40px' },
      }]
      : [];

    return [
      ...fields,
      {
        label: this.$t('returnOrders.createdAt'),
        key: 'createdAt',
        sortable: true,
        formatter: (value: string) => getShowingDate(value)
      },
      {
        label: this.$t('returnOrders.amount'),
        key: 'amount',
        thClass: 'text-right',
        tdClass: 'text-right',
        formatter: (value: number) => numberFormatter(value),
      },
      {
        label: this.$t('returnOrders.status'),
        key: 'status',
        formatter: (value: string) => getReturnOrderStatus(value)
      },
      {
        label: this.$t('returnOrders.phoneNumber'),
        key: 'phoneNumber',
      },
      {
        label: this.$t('returnOrders.telecomProviderId'),
        key: 'telecomProviderId',
      },
      {
        label: this.$t('returnOrders.id'),
        key: 'id',
      },
      {
        label: this.$t('returnOrders.externalPaymentId'),
        key: 'externalPaymentId',
      },
      {
        label: this.$t('returnOrders.externalPaymentStatus'),
        key: 'externalPaymentStatus',
        formatter: (value: string) => getExternalPaymentStatus(value)
      },
      {
        label: this.$t('returnOrders.pspInvoiceOrderId'),
        key: 'pspInvoiceOrderId',
      },
      {
        label: this.$t('returnOrders.pspInvoiceOrderId'),
        key: 'pspInvoiceOrderId',
      },
      {
        label: this.$t('returnOrders.partnerAccountFrom'),
        key: 'partnerAccountFrom',
      },
      {
        label: this.$t('returnOrders.partnerAccountTo'),
        key: 'partnerAccountTo',
      },
      {
        label: this.$t('returnOrders.commission'),
        key: 'commission',
        formatter: (_1, _2, item: ReturnOrderType) => {
          return `${item.percentCommission}% + ${numberFormatter(item.fixedCommissionAmount)} | ${numberFormatter(item.minCommissionAmount)}`
        },
      },
      {
        label: this.$t('returnOrders.comment'),
        key: 'commentText',
        formatter: (value: string) => removeTags(value).slice(0, 50) || ''
      },
      {
        label: '',
        key: 'actions',
        thStyle: { width: '40px' },
      },
    ];
  }

  getActionsParams(data): ActionsCellRendererParams<ReturnOrderType> {
    return {
      data: data.item,
      buttons: [
        {
          type: 'comment',
          actionCallback: (params: ActionsCellRendererParams<ReturnOrderType>) => {
            this.showCommentDialog(params.data.id, ENTITY_RETURN_ORDER);
          },
          btnContent: `<div class="comment-count ${data.item.commentsCount ? 'filled' : ''}">${data.item.commentsCount || 0}</div>`,
          show: () => this.canEntityCreate(ENTITY_COMMENTS)
        },
        {
          type: 'details',
          actionCallback: (params: ActionsCellRendererParams<ReturnOrderType>) => {
            this.$router.push({
              name: 'details-card',
              query: {
                returnOrderId: params.data.id.toString()
              }
            })
          },
          btnContent: '<div class="circle-icon">d</div>',
        },
        {
          type: 'transactions',
          actionCallback: (params: ActionsCellRendererParams<ReturnOrderType>) => {
            this.$router.push({
              name: 'transactions',
              query: {
                returnOrderId: params.data.id.toString()
              }
            })
          },
          btnContent: '<div class="circle-icon">t</div>',
        },
        {
          type: 'fe-info',
          actionCallback: (params: ActionsCellRendererParams<ReturnOrderType>) => {
            this.showInfoDialog(params.data, title);
          },
          btnContent: '<div class="circle-icon">i</div>',
        },
      ]
    };
  }

  onExport() {
    this.xlsExport({
      title,
      action: this.getReturnOrders,
      sort: DEFAULT_SORT,
      content: [
        {
          label: this.$t('returnOrders.createdAt'),
          getValue: (item: ReturnOrderType) => getShowingDate(item.createdAt)
        },
        {
          label: this.$t('returnOrders.amount'),
          getValue: (item: ReturnOrderType) => this.numberFormatter(item.amount)
        },
        {
          label: this.$t('returnOrders.status'),
          getValue: (item: ReturnOrderType) => getReturnOrderStatus(item.status)
        },
        {
          label: this.$t('returnOrders.phoneNumber'),
          getValue: (item: ReturnOrderType) => item.phoneNumber
        },
        {
          label: this.$t('returnOrders.telecomProviderId'),
          getValue: (item: ReturnOrderType) => item.telecomProviderId
        },
        {
          label: this.$t('returnOrders.id'),
          getValue: (item: ReturnOrderType) => item.id
        },
        {
          label: this.$t('returnOrders.externalId'),
          getValue: (item: ReturnOrderType) => item.externalId
        },
        {
          label: this.$t('returnOrders.externalPaymentId'),
          getValue: (item: ReturnOrderType) => item.externalPaymentId
        },
        {
          label: this.$t('returnOrders.externalPaymentStatus'),
          getValue: (item: ReturnOrderType) => getExternalPaymentStatus(item.externalPaymentStatus)
        },
        {
          label: this.$t('returnOrders.pspInvoiceOrderId'),
          getValue: (item: ReturnOrderType) => item.pspInvoiceOrderId
        },
        {
          label: this.$t('returnOrders.partnerAccountFrom'),
          getValue: (item: ReturnOrderType) => item.partnerAccountFrom
        },
        {
          label: this.$t('returnOrders.partnerEndpointFrom'),
          getValue: (item: ReturnOrderType) => item.partnerEndpointFrom
        },
        {
          label: this.$t('returnOrders.partnerAccountTo'),
          getValue: (item: ReturnOrderType) => item.partnerAccountTo
        },
        {
          label: this.$t('returnOrders.partnerEndpointTo'),
          getValue: (item: ReturnOrderType) => item.partnerEndpointTo
        },
        {
          label: this.$t('returnOrders.percentCommission'),
          getValue: (item: ReturnOrderType) => item.percentCommission
        },
        {
          label: this.$t('returnOrders.fixedCommissionAmount'),
          getValue: (item: ReturnOrderType) => numberFormatter(item.fixedCommissionAmount)
        },
        {
          label: this.$t('returnOrders.minCommissionAmount'),
          getValue: (item: ReturnOrderType) => numberFormatter(item.minCommissionAmount)
        },
        {
          label: this.$t('returnOrders.comment'),
          getValue: (item: ReturnOrderType) => item.commentText
        },
      ]
    })
  }

  get canReadTransactions() {
    return this.canEntityRead(ENTITY_TRANSACTION);
  }

  get canReadPIO() {
    return this.canEntityRead(ENTITY_PSP_INVOICE_ORDER);
  }

  getUrl(action: string) {
    return `${window.location.origin}/${action}`
  }

  onClickTitle() {
    if (this.toggled) {
      this.isShowGrid = !this.isShowGrid;
    }
  }
}
